import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area
} from 'recharts';
import {
  Zap, Calendar, Heart, ShieldAlert, Award, Clock, Activity, BarChart2,
  Sparkles, Share2, Target, AlertTriangle, Check, CheckCircle2, ChevronRight,
  Flame, Compass, ShieldCheck, FileText, Ban, Info
} from 'lucide-react';
import InfoTooltip from './InfoTooltip';
import MorningBriefingBanner from './MorningBriefingBanner';
import MedicalDisclaimer from './MedicalDisclaimer';
import DecisionDashboard from './DecisionDashboard';
import MechanicalExposureCard from './MechanicalExposureCard';
import DataQualityCard from './DataQualityCard';
import heroSaludBg from '../assets/hero_salud.png';

const TrainingReadiness = ({ data, userProfile, isCoachView, briefing, briefingLoading, dashboardMode = 'essential', phase = 'pre_workout', activities = [] }) => {
  const [activeTab, setActiveTab] = useState('readiness'); // 'readiness', 'hrv_corridor', 'strain_balance'

  // En Modo Esencial, fijar siempre la pestaña principal de Predisposición 360°
  useEffect(() => {
    if (dashboardMode === 'essential') {
      setActiveTab('readiness');
    }
  }, [dashboardMode]);

  if (!data) return null;
  if (!data.hrvData || !data.sleepData || !data.rhrData) {
    return (
      <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.5rem', textAlign: 'center', justifyContent: 'center', minHeight: '300px' }}>
        <h2 className="text-xl" style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
          <Activity size={20} style={{ color: 'var(--text-secondary)' }} /> Predisposición Diaria
        </h2>
        <div style={{ color: 'var(--text-secondary)' }}>
          <ShieldAlert size={40} style={{ margin: '0 auto', opacity: 0.5, marginBottom: '1rem' }} />
          <p style={{ marginBottom: '0.5rem' }}>Faltan datos de recuperación</p>
          <p className="text-xs">Para calcular tu Predisposición Diaria necesitamos conocer tu sueño, pulso en reposo (FCR) y variabilidad (HRV).</p>
          <p className="text-xs" style={{ marginTop: '0.5rem' }}>Conecta una fuente de salud en Ajustes o registra tus métricas manualmente desde el menú.</p>
        </div>
      </div>
    );
  }

  const {
    readinessScore,
    sleepData,
    rhrData,
    hrvData,
    spo2Data,
    strainScore,
    strainHistory,
    withingsConnected,
    appleHealthConnected,
    googleHealthConnected
  } = data || {};

  const todayIsoStr = new Date().toISOString().split('T')[0];
  const hasTrainedToday = phase === 'post_workout' || !!(data?.dailyPrescription?.hasTrainedToday) || (activities && activities.some(a => {
    const d = a.startDate || a.start_date;
    return d ? new Date(d).toISOString().startsWith(todayIsoStr) : false;
  }));

  // CONTRATO CANÓNICO v3.0: Backend como única fuente de verdad fisiológica
  const canonicalState = data?.canonicalState || data?.decisionState?.state || data?.dailyPrescription?.canonicalState || (hasTrainedToday ? 'COMPLETED' : 'READY');
  const readiness = data?.readiness ?? readinessScore ?? 50;
  const confidence = data?.confidence || data?.readinessDetails?.confidence || (data?.isSimulatedToday ? 'Baja' : 'Alta');
  const safetyVetoes = data?.safetyVetoes || data?.dailyPrescription?.alerts || [];
  const rulesTriggered = data?.rulesTriggered || (data?.dailyPrescription?.pattern ? [data.dailyPrescription.pattern] : []);
  const why = data?.why || (data?.dailyPrescription?.expectedEvolution ? [data.dailyPrescription.expectedEvolution] : []);
  const whatToAvoid = data?.whatToAvoid || data?.dailyPrescription?.whatToAvoid || 'Sin restricciones especiales';
  const recommendation = data?.recommendation || data?.decisionState?.recommendation || {
    category: data?.dailyPrescription?.category || (hasTrainedToday ? 'COMPLETADO' : 'NORMAL'),
    targetDuration: data?.dailyPrescription?.duration || '45 - 60 min',
    targetZones: data?.dailyPrescription?.targetZones || 'Zona aeróbica Z1-Z2',
    intervals: data?.dailyPrescription?.intervals || 'No'
  };

  // Prescripción del día alimentada directamente por el Backend
  const dailyPrescription = data?.dailyPrescription || {
    canonicalState,
    pattern: rulesTriggered[0] || 'NORMAL',
    patternName: `Patrón: ${canonicalState}`,
    category: recommendation.category || 'NORMAL',
    duration: recommendation.targetDuration || recommendation.duration || '45 - 60 min',
    targetZones: recommendation.targetZones || 'Zona aeróbica Z1-Z2',
    intervals: recommendation.intervals || 'No',
    whatToAvoid: whatToAvoid,
    expectedEvolution: why[0] || 'Asimilación biológica determinada por el motor central',
    alerts: safetyVetoes
  };

  const resolvedPhase = dailyPrescription.physiologicalPhase || data?.physiologicalPhase || (
    hasTrainedToday ? { label: 'Asimilación Aguda', color: '#10b981', reason: 'Cuota diaria completada. Supercompensación metabólica y descanso.' } :
    (dailyPrescription.category === 'INTENSO' || canonicalState === 'HIGH_PERFORMANCE') ? { label: 'Supercompensación (48h)', color: '#00e5ff', reason: 'Ventana de asimilación completada; reservas energéticas y tono parasimpático en su pico de disponibilidad.' } :
    (dailyPrescription.category === 'SUAVE Z1-Z2' || canonicalState === 'LOW_AEROBIC') ? { label: 'Recuperación Acelerada', color: '#38bdf8', reason: 'Tu descanso nocturno y VFC en rango óptimo han reactivado tu disponibilidad antes de la estimación matemática teórica.' } :
    dailyPrescription.category === 'RECUPERACIÓN' ? { label: 'Descarga Activa', color: '#a78bfa', reason: 'Sesión regenerativa suave para oxigenar tejidos sin añadir fatiga.' } :
    dailyPrescription.category === 'DESCANSO' ? { label: 'Asimilación y Descanso', color: '#f87171', reason: 'Reposo preventivo prioritario para restaurar el equilibrio sistémico.' } :
    { label: 'Equilibrio Fisiológico', color: '#10b981', reason: 'Carga de entrenamiento y biomarcadores basales alineados en pasillo óptimo.' }
  );

  const effectiveBriefing = briefing || (
    dailyPrescription
      ? `${dailyPrescription.patternName ? dailyPrescription.patternName + '. ' : ''}${dailyPrescription.expectedEvolution || 'Dosis aconsejada para asimilar la carga biológica.'} Pauta del día: ${dailyPrescription.category} (${dailyPrescription.duration || '30-45 min'}) estrictamente en ${dailyPrescription.targetZones}. ${dailyPrescription.whatToAvoid ? 'Evitar: ' + dailyPrescription.whatToAvoid + '.' : ''}`
      : 'Evaluando métricas fisiológicas para prescribir la sesión óptima del día.'
  );

  const readinessDetails = data.readinessDetails || {
    confidence: confidence,
    positives: why.length > 0 ? why : ['Equilibrio autonómico verificado por el motor'],
    negatives: safetyVetoes.length > 0 ? safetyVetoes : []
  };

  const getCategoryTheme = (category) => {
    switch (category) {
      case 'COMPLETADO':
      case 'SESIÓN DE HOY COMPLETADA':
        return { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)', icon: CheckCircle2, text: '✅ SESIÓN COMPLETADA', badgeColor: '#10b981' };
      case 'INTENSO':
        return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.35)', icon: Flame, text: '⚡ INTENSO', badgeColor: '#ef4444' };
      case 'NORMAL':
        return { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.35)', icon: Activity, text: '🟢 NORMAL', badgeColor: '#10b981' };
      case 'MODERADO':
        return { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.35)', icon: Compass, text: '🔵 MODERADO', badgeColor: '#3b82f6' };
      case 'SUAVE Z1-Z2':
        return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.35)', icon: Clock, text: '🟡 SUAVE Z1-Z2', badgeColor: '#f59e0b' };
      case 'RECUPERACIÓN':
        return { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.35)', icon: Sparkles, text: '🟣 RECUPERACIÓN', badgeColor: '#8b5cf6' };
      case 'DESCANSO':
      default:
        return { color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)', border: 'rgba(248, 113, 113, 0.35)', icon: Ban, text: '🔴 DESCANSO TOTAL', badgeColor: '#f87171' };
    }
  };

  const catTheme = getCategoryTheme(dailyPrescription.category);

  // Tema del Dial y Diagnóstico derivado del estado canónico del Backend
  const getReadinessThemeByState = (state, category, vetoes = []) => {
    if (userProfile?.preferences?.recoveryMode) {
      return { 
        label: 'Modo Recuperación 🏥', 
        color: '#10b981', 
        desc: 'Priorizando la regeneración muscular y fisioterapia activa.', 
        tip: 'Enfócate en estiramientos, movilidad o descanso activo. Escucha a tu cuerpo.' 
      };
    }

    if (vetoes.length > 0 || state === 'RECOVERY' || category === 'DESCANSO' || category === 'DESCANSO_TOTAL') {
      return {
        label: 'Alerta Preventiva 🚨',
        color: '#ef4444',
        desc: vetoes[0] || 'Señales de fatiga sistémica o sobrecarga. La prioridad fisiológica es descanso total.',
        tip: 'Reposo en cama y descanso pasivo. Protege tu recuperación.'
      };
    }

    if (state === 'LOW_AEROBIC' || category === 'SUAVE Z1-Z2' || category === 'RECUPERACIÓN') {
      return {
        label: 'Fatiga Acumulada ⚡',
        color: '#f59e0b',
        desc: 'Estrés agudo por bloque de carga previa. El cuerpo necesita asimilar y reparar fibras.',
        tip: 'Sesión aeróbica suave (Z1/Z2) o rodaje regenerativo por debajo de VT1.'
      };
    }

    if (state === 'MODULATED' || category === 'MODERADO') {
      return {
        label: 'Limitado / Controlado 💤',
        color: '#f59e0b',
        desc: 'El organismo tolera volumen moderado, pero se modula la intensidad para proteger la asimilación central.',
        tip: 'Sesión aeróbica suave/moderada en Z2. Cero series lácticas o tiradas largas.'
      };
    }

    if (state === 'HIGH_PERFORMANCE' || category === 'INTENSO') {
      return {
        label: 'Sobresaliente 🏆',
        color: '#00e5ff',
        desc: '¡Sistema nervioso súper compensado y biomarcadores óptimos! Ventana biológica para ritmos vivos o series.',
        tip: 'Luz verde para cualquier reto hoy. ¡Sesión de calidad!'
      };
    }

    if (state === 'COMPLETED' || category === 'COMPLETADO') {
      return {
        label: 'Sesión Completada ✅',
        color: '#10b981',
        desc: 'Cuota de entrenamiento del día alcanzada. Organismo en fase de supercompensación y regeneración.',
        tip: 'Prioriza rehidratación y descanso pasivo.'
      };
    }

    return {
      label: 'Óptimo 🟢',
      color: '#10b981',
      desc: 'Tu cuerpo ha asimilado la fatiga. Buen estado aeróbico y descanso reparador.',
      tip: 'Buen día para entrenamiento programado o fondo aeróbico.'
    };
  };

  const theme = getReadinessThemeByState(canonicalState, dailyPrescription.category, safetyVetoes);

  const realTsb = data?.decisionState?.inputs?.tsb ?? data?.tsb ?? data?.todayPmc?.tsb ?? 0;
  const realCtl = data?.decisionState?.inputs?.ctl ?? data?.ctl ?? data?.todayPmc?.ctl ?? 0;
  const realAtl = data?.decisionState?.inputs?.atl ?? data?.atl ?? data?.todayPmc?.atl ?? 0;

  const resolvedDecisionState = {
    state: data?.decisionState?.state || canonicalState,
    confidence: typeof confidence === 'number' ? confidence : (confidence === 'Alta' ? 90 : confidence === 'Media' ? 70 : 40),
    inputs: {
      readiness: data?.decisionState?.inputs?.readiness ?? readiness,
      tsb: realTsb,
      ctl: realCtl,
      atl: realAtl,
      mechanicalExposureScore: data?.decisionState?.inputs?.mechanicalExposureScore ?? data?.mechanicalExposureScore ?? (data?.mechanicalExposure?.score ?? 0),
      mechanicalExposureLevel: data?.decisionState?.inputs?.mechanicalExposureLevel ?? data?.mechanicalExposureLevel ?? (data?.mechanicalExposure?.level ?? 'LOW')
    },
    recommendation: data?.decisionState?.recommendation || {
      category: recommendation.category || dailyPrescription.category || 'NORMAL',
      targetDuration: recommendation.targetDuration || dailyPrescription.duration || '50 - 75 min',
      targetZones: recommendation.targetZones || dailyPrescription.targetZones || 'Zona 2-3 aeróbica',
      allowedZones: recommendation.allowedZones || (canonicalState === 'HIGH_PERFORMANCE' ? ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'] : ['Z1', 'Z2', 'Z3']),
      bannedZones: recommendation.bannedZones || (canonicalState === 'RECOVERY' ? ['Z2', 'Z3', 'Z4', 'Z5'] : [])
    },
    why: data?.decisionState?.why || (why.length > 0 ? why : ['Biomarcadores basales autonómicos estables', 'Sueño reparador registrado']),
    watch: data?.decisionState?.watch || (Array.isArray(whatToAvoid) ? whatToAvoid : [whatToAvoid])
  };

  // 1. Exposición mecánica real (sin inventar métricas ni picos ficticios)
  const resolvedExposure = useMemo(() => {
    if (data?.mechanicalExposure && typeof data.mechanicalExposure.score === 'number') {
      return data.mechanicalExposure;
    }

    const acts = Array.isArray(activities) ? activities : [];
    const now = Date.now();
    let acute7d = 0;
    let chronic28d = 0;

    acts.forEach(act => {
      const actDate = new Date(act.start_date || act.startDate).getTime();
      const daysAgo = Math.max(0, Math.floor((now - actDate) / (1000 * 60 * 60 * 24)));
      if (daysAgo > 42) return;

      const sportLower = (act.sport_type || act.type || '').toLowerCase();
      const isRun = sportLower.includes('run');
      const distKm = (act.distance || 0) / 1000;
      const elevGain = act.total_elevation_gain || act.totalElevationGain || 0;
      const mech = isRun ? Math.round(distKm * 10 * (1.0 + Math.min(0.5, elevGain / 500))) : 0;

      if (daysAgo <= 7) acute7d += mech;
      if (daysAgo <= 28) chronic28d += mech;
    });

    const chronicWeeklyAvg = (chronic28d / 4) || 1;
    const acwrDescriptive = chronic28d > 0 ? parseFloat((acute7d / chronicWeeklyAvg).toFixed(2)) : 1.0;

    if (chronic28d === 0) {
      return {
        score: 15,
        level: 'LOW',
        acute7d: 0,
        chronic28d: 0,
        acwrDescriptive: 1.0,
        drivers: ['Sin sesiones de impacto articular de carrera en las últimas 4 semanas', 'Bajo estrés osteoarticular']
      };
    }

    let score = 25;
    const drivers = [];
    if (acwrDescriptive > 1.35) {
      score += Math.min(45, (acwrDescriptive - 1.35) * 60);
      drivers.push(`Pico agudo de volumen (+${Math.round((acwrDescriptive - 1) * 100)}%)`);
    } else if (acwrDescriptive < 0.8) {
      score = Math.max(15, score - 10);
      drivers.push('Semana de descarga articular (-20%)');
    } else {
      score = 30;
      drivers.push('Volumen y estímulo dentro del pasillo habitual');
    }

    const level = score > 60 ? 'HIGH_EXPOSURE' : score > 40 ? 'ELEVATED' : score > 20 ? 'MODERATE' : 'LOW';

    return {
      score: Math.round(score),
      level,
      acute7d: Math.round(acute7d),
      chronic28d: Math.round(chronic28d),
      acwrDescriptive,
      drivers
    };
  }, [data?.mechanicalExposure, activities]);

  // 2. Calidad de Telemetría real (Data Confidence Layer) — NUNCA inventar sensores ni porcentajes
  const resolvedQuality = useMemo(() => {
    if (data?.measurementQuality && data.measurementQuality.components && Object.keys(data.measurementQuality.components).length > 0) {
      return data.measurementQuality;
    }

    const comps = {};
    const flags = [];

    // Wearables de salud y sueño
    if (data?.sleepData && (data.sleepData.duration > 0 || data.sleepData.score > 0)) {
      const hasStages = Boolean(data.sleepData.stages && (data.sleepData.stages.deep || data.sleepData.stages.rem));
      comps.sleep = {
        confidenceScore: hasStages ? 0.90 : 0.78,
        label: 'Monitorización Sueño'
      };
    }

    if (data?.rhrData && data.rhrData.current > 0) {
      comps.rhr = {
        confidenceScore: 0.92,
        label: 'Pulso Reposo (FCR)'
      };
    }

    if (data?.hrvData && data.hrvData.current > 0) {
      comps.hrv = {
        confidenceScore: 0.90,
        label: 'Variabilidad (HRV)'
      };
    }

    if (data?.spo2Data && data.spo2Data.current > 0) {
      const isLow = data.spo2Data.current < 92;
      comps.spo2 = {
        confidenceScore: isLow ? 0.65 : 0.88,
        label: 'Saturación SpO2'
      };
      if (isLow) flags.push(`Saturación SpO2 baja detectada (${data.spo2Data.current}%)`);
    }

    // Sensores de actividad (última sesión)
    const recentAct = (Array.isArray(activities) && activities.length > 0) ? activities[0] : null;
    if (recentAct) {
      if (recentAct.has_heartrate || (recentAct.average_heartrate && recentAct.average_heartrate > 0)) {
        comps.hr = {
          confidenceScore: 0.95,
          label: 'Pulso Cardíaco'
        };
      }

      const sportLower = (recentAct.sport_type || recentAct.type || '').toLowerCase();
      const isIndoor = sportLower.includes('virtual') || sportLower.includes('indoor') || sportLower.includes('weight') || sportLower.includes('workout');
      if (recentAct.distance > 0 && !isIndoor) {
        comps.gps = {
          confidenceScore: 0.94,
          label: 'GPS / Trayectoria'
        };
      }

      // Potenciómetro: ESTRICTAMENTE solo si hay sensor físico verificado (NUNCA estimado)
      const hasRealPower = Boolean(
        (recentAct.device_watts === true || recentAct.deviceWatts === true) &&
        (recentAct.average_watts > 0 || recentAct.averageWatts > 0 || recentAct.weighted_average_watts > 0 || recentAct.normalizedPower > 0) &&
        !recentAct.estimated_power
      );
      if (hasRealPower) {
        comps.power = {
          confidenceScore: 0.92,
          label: 'Potenciómetro'
        };
      }

      if (recentAct.average_cadence > 0) {
        comps.cadence = {
          confidenceScore: 0.92,
          label: 'Cadencia'
        };
      }
    }

    const compValues = Object.values(comps);
    if (compValues.length === 0) {
      return {
        globalScore: 0,
        confidenceTier: 'NO_DATA',
        components: {},
        flags: ['Sin sensores de telemetría activos hoy']
      };
    }

    const avgScore = compValues.reduce((sum, c) => sum + c.confidenceScore, 0) / compValues.length;
    const globalScore = Math.round(avgScore * 100);
    const tier = globalScore >= 80 ? 'HIGH' : globalScore >= 60 ? 'MEDIUM' : 'LOW';

    return {
      globalScore,
      confidenceTier: tier,
      components: comps,
      flags
    };
  }, [data?.measurementQuality, data?.sleepData, data?.rhrData, data?.hrvData, data?.spo2Data, activities]);



  // Formatear datos para el gráfico de corredor de HRV
  // hrvData.history es de 28 días
  const hrvChartData = hrvData?.history ? hrvData.history.map((val, idx) => {
    const minVal = hrvData.corridorMin[idx] || 45;
    const maxVal = hrvData.corridorMax[idx] || 65;
    return {
      day: idx + 1,
      hrv: val,
      min: minVal,
      max: maxVal,
      corridor: [minVal, maxVal],
      rolling7: hrvData.rolling7 ? hrvData.rolling7[idx] : val
    };
  }) : [];

  // Puntos clave del dial circular (Ready gauge)
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (readinessScore / 100) * circumference;

  return (
    <section
      className="glass-panel"
      role="region"
      aria-label={`Panel de Predisposición Diaria: ${readinessScore} sobre 100 (${theme.label}). Prescripción recomendada: ${dailyPrescription.category} (${dailyPrescription.duration})`}
      style={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '1.25rem', 
      padding: '1.5rem',
      backgroundColor: 'rgba(15,23,42,1)',
      position: 'relative',
      overflow: 'hidden',
      borderTop: `1px solid ${theme.color}50` // Subtle top border matching theme
    }}>
      {/* Resumen textual accesible para lectores de pantalla / VoiceOver (A-09) */}
      <div style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }} aria-live="polite">
        {`Resumen de Predisposición Fisiológica de hoy: puntuación ${readinessScore} sobre 100, estado ${theme.label}. ${theme.desc} Dosis recomendada: ${dailyPrescription.category}, duración ${dailyPrescription.duration}, zonas objetivo ${dailyPrescription.targetZones}. Qué evitar hoy: ${dailyPrescription.whatToAvoid}.`}
      </div>
      
      {/* Background Image with Edge-to-Edge Fade */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, height: '65%',
        backgroundImage: `linear-gradient(to bottom, rgba(15,23,42,0) 0%, rgba(15,23,42,0.5) 60%, rgba(15,23,42,1) 100%), url(${heroSaludBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        opacity: 0.45,
        filter: 'grayscale(30%)',
        zIndex: 0,
        pointerEvents: 'none'
      }}></div>

      {/* Glow ambient background based on theme score */}
      <div style={{ position: 'absolute', top: '10%', right: '-10%', width: '200px', height: '200px', background: theme.color, filter: 'blur(100px)', opacity: 0.25, borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }}></div>
      
      {/* HEADER CON TABS */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', position: 'relative', zIndex: 1 }}>
        <div>
          <h2 className="text-xl" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Activity size={20} style={{ color: theme.color }} /> Predisposición Diaria
            <InfoTooltip position="bottom" text={`La Predisposición Diaria (0-100) es un indicador 360° calculado ponderando 5 pilares biológicos y de carga:\n\n1. 🛌 Calidad de Sueño (35%): Horas, eficiencia y despertares.\n2. 🫀 HRV rMSSD (25%): Equilibrio del sistema nervioso vs. pasillo de 28d.\n3. ⚡ Frescura / TSB (25%): Balance entre fatiga (ATL) y fitness (CTL).\n4. 💚 FC Reposo (15%): Estabilidad del pulso matutino basal.\n5. 🫁 Oxígeno SpO2: Oxigenación del SNC y alerta de fatiga sistémica.`} />
          </h2>
          <p className="text-muted text-xs" style={{ margin: '2px 0 0 0' }}>
            {googleHealthConnected
              ? '⚡ Biometría Google Health + Carga Strava'
              : (appleHealthConnected 
                  ? '⚡ Biometría Apple Health + Carga Strava' 
                  : (withingsConnected 
                      ? '⚡ Biometría Withings + Carga Strava' 
                      : '🛌 Estimación algorítmica'))}
          </p>
          {data?.isSimulatedToday && (
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#f59e0b', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(245, 158, 11, 0.1)', padding: '0.3rem 0.6rem', borderRadius: '4px' }}>
              ⚠️ Datos de ayer. Abre la App para sincronizar hoy.
            </div>
          )}
        </div>

        {dashboardMode === 'pro' && (
          <div className="tab-container" role="tablist" aria-label="Vistas de predisposición fisiológica" style={{ display: 'flex', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '3px', border: '1px solid var(--glass-border)' }}>
            <button 
              role="tab"
              aria-selected={activeTab === 'readiness'}
              onClick={() => setActiveTab('readiness')} 
              className={`tab-btn ${activeTab === 'readiness' ? 'active' : ''}`}
              style={{
                padding: '0.35rem 0.65rem', fontSize: '0.72rem', borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: activeTab === 'readiness' ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: activeTab === 'readiness' ? 'white' : 'var(--text-secondary)', fontWeight: 600, transition: 'all 0.2s'
              }}
            >
              Predisposición 360°
            </button>
            <button 
              role="tab"
              aria-selected={activeTab === 'hrv_corridor'}
              onClick={() => setActiveTab('hrv_corridor')} 
              className={`tab-btn ${activeTab === 'hrv_corridor' ? 'active' : ''}`}
              style={{
                padding: '0.35rem 0.6rem', fontSize: '0.72rem', borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: activeTab === 'hrv_corridor' ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: activeTab === 'hrv_corridor' ? 'white' : 'var(--text-secondary)', fontWeight: 600, transition: 'all 0.2s'
              }}
            >
              Pasillo HRV
            </button>
            <button 
              role="tab"
              aria-selected={activeTab === 'strain_balance'}
              onClick={() => setActiveTab('strain_balance')} 
              className={`tab-btn ${activeTab === 'strain_balance' ? 'active' : ''}`}
              style={{
                padding: '0.35rem 0.6rem', fontSize: '0.72rem', borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: activeTab === 'strain_balance' ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: activeTab === 'strain_balance' ? 'white' : 'var(--text-secondary)', fontWeight: 600, transition: 'all 0.2s'
              }}
            >
              Strain Balance
            </button>
            <button 
              role="tab"
              aria-selected={activeTab === 'decision'}
              onClick={() => setActiveTab('decision')} 
              className={`tab-btn ${activeTab === 'decision' ? 'active' : ''}`}
              style={{
                padding: '0.35rem 0.65rem', fontSize: '0.72rem', borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: activeTab === 'decision' ? 'rgba(56,189,248,0.15)' : 'transparent',
                color: activeTab === 'decision' ? '#38bdf8' : 'var(--text-secondary)', fontWeight: 700, transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <Compass size={12} /> Motor Decisión
            </button>
          </div>
        )}
      </div>

      {/* RENDER TAB: TRAINING READINESS GAUGE */}
      {activeTab === 'readiness' && (
        <>
        {/* FILA SUPERIOR: DUAL INDEPENDENT RESULTS (0-100 Predisposición Biológica vs Prescripción Dosis) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 330px), 1fr))',
          gap: '1.25rem',
          alignItems: 'stretch'
        }}>
          
          {/* RESULTADO 1: SCORE DE PREDISPOSICIÓN BIOLÓGICA (0-100) */}
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '16px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '1rem',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Cabecera Indicador */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Activity size={13} style={{ color: theme.color }} /> 1. Estado Biológico
              </span>
              <span style={{
                fontSize: '0.7rem',
                background: readinessDetails.confidence === 'Alta' ? 'rgba(16, 185, 129, 0.12)' : readinessDetails.confidence === 'Media' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                color: readinessDetails.confidence === 'Alta' ? '#34d399' : readinessDetails.confidence === 'Media' ? '#fbbf24' : '#f87171',
                border: `1px solid ${readinessDetails.confidence === 'Alta' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(245, 158, 11, 0.25)'}`,
                padding: '2px 8px', borderRadius: '12px', fontWeight: 600
              }}>
                Confianza {readinessDetails.confidence}
              </span>
            </div>

            {/* Gauge Dial & Diagnóstico */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <div style={{ position: 'relative', width: '135px', height: '135px' }}>
                <svg width="100%" height="100%" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="7" />
                  <circle
                    cx="60" cy="60" r={radius} fill="none"
                    stroke={theme.color}
                    strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{ transition: 'stroke-dashoffset 1s ease-out', filter: `drop-shadow(0 0 4px ${theme.color}40)` }}
                  />
                </svg>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', textAlign: 'center'
                }}>
                  <span style={{ fontSize: '2.4rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{readinessScore}</span>
                  <span style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '3px', fontWeight: 600 }}>Cuerpo</span>
                </div>
              </div>

              <div style={{ textAlign: 'center', width: '100%' }}>
                <span style={{
                  fontSize: '0.82rem', background: `${theme.color}15`, color: theme.color,
                  border: `1px solid ${theme.color}25`, padding: '4px 14px', borderRadius: '20px', fontWeight: 700, display: 'inline-block'
                }}>
                  {theme.label}
                </span>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-primary)', marginTop: '0.6rem', lineHeight: 1.4, fontWeight: 500, margin: '0.6rem 0 0 0' }}>
                  {theme.desc}
                </p>
              </div>
            </div>

            {/* Factores Clave (Positivos y Limitantes) */}
            {(readinessDetails.positives?.length > 0 || readinessDetails.negatives?.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', textAlign: 'left', width: '100%' }}>
                {readinessDetails.positives?.slice(0, 2).map((pos, idx) => (
                  <div key={`pos-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: '#34d399', background: 'rgba(16, 185, 129, 0.08)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                    <Check size={13} style={{ flexShrink: 0 }} /> <span>{pos}</span>
                  </div>
                ))}
                {readinessDetails.negatives?.slice(0, 2).map((neg, idx) => (
                  <div key={`neg-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: '#fbbf24', background: 'rgba(245, 158, 11, 0.08)', padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0 }} /> <span>{neg}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RESULTADO 2: PRESCRIPCIÓN DE ENTRENAMIENTO DEL DÍA */}
          <div style={{
            background: `radial-gradient(ellipse at top left, ${catTheme.color}14 0%, rgba(15, 23, 42, 0.95) 60%, rgba(11, 20, 34, 0.98) 100%)`,
            border: `1px solid ${catTheme.border}`,
            borderRadius: '16px',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '0.85rem',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 8px 25px ${catTheme.color}15`
          }}>
            <div style={{
              position: 'absolute', top: '-30px', right: '-30px', width: '130px', height: '130px',
              background: catTheme.color, filter: 'blur(70px)', opacity: 0.15, borderRadius: '50%', pointerEvents: 'none'
            }} />

            {/* Header Prescription */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', position: 'relative', zIndex: 1 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <div style={{ padding: '5px', background: `${catTheme.color}20`, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isCoachView ? <ShieldCheck size={16} color={catTheme.color} /> : <Target size={16} color={catTheme.color} />}
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isCoachView ? '2. Triage & Restricciones Fisiológicas' : '2. Prescripción de Entrenamiento'}
                  </span>
                  {dailyPrescription.thresholdsUsed?.hasMedicalTest && (
                    <span style={{ fontSize: '0.65rem', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '1px 6px', borderRadius: '10px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <ShieldCheck size={11} /> Test Médico
                    </span>
                  )}
                </div>
                <h3 style={{ margin: '4px 0 0 0', fontSize: '1.15rem', fontWeight: 800, color: 'white' }}>
                  {isCoachView ? 'Guía de Carga y Restricciones' : 'Dosis Recomendada Hoy'}
                </h3>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '0.78rem',
                  background: catTheme.bg,
                  color: catTheme.badgeColor,
                  border: `1px solid ${catTheme.border}`,
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontWeight: 800,
                  boxShadow: `0 0 10px ${catTheme.color}25`
                }}>
                  {catTheme.text}
                </span>
                <span style={{
                  fontSize: '0.7rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '3px 8px',
                  borderRadius: '20px',
                  fontWeight: 600
                }}>
                  {dailyPrescription.patternName}
                </span>
                {resolvedPhase && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <span style={{
                      fontSize: '0.7rem',
                      background: `${resolvedPhase.color || '#00e5ff'}18`,
                      color: resolvedPhase.color || '#00e5ff',
                      border: `1px solid ${resolvedPhase.color || '#00e5ff'}40`,
                      padding: '3px 8px',
                      borderRadius: '20px',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Sparkles size={11} /> {resolvedPhase.label}
                    </span>
                    {resolvedPhase.reason && (
                      <InfoTooltip text={`Fase: ${resolvedPhase.label}\n\n${resolvedPhase.reason}`} position="bottom" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Prescriptive Grid Elements */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.6rem',
              position: 'relative',
              zIndex: 1
            }}>
              {/* Ventana de Carga / Duración */}
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '0.65rem 0.8rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={11} color="var(--health-cyan)" /> {isCoachView ? 'Ventana de Carga' : 'Duración'}
                </span>
                <div style={{ fontSize: isCoachView ? '0.92rem' : '1.05rem', fontWeight: 700, color: 'white', marginTop: '3px', lineHeight: 1.2 }}>
                  {isCoachView 
                    ? (dailyPrescription.category === 'INTENSO' ? 'Apta para Calidad / VT2' 
                       : dailyPrescription.category === 'NORMAL' || dailyPrescription.category === 'MODERADO' ? 'Carga Asimilable (Z1-Z3)' 
                       : dailyPrescription.category === 'SUAVE Z1-Z2' ? 'Estrictamente < VT1' 
                       : dailyPrescription.category === 'RECUPERACIÓN' ? 'Regenerativa / Descanso Activo' 
                       : dailyPrescription.category === 'DESCANSO' ? 'Descanso Total Preventivo' 
                       : dailyPrescription.duration)
                    : dailyPrescription.duration}
                </div>
              </div>

              {/* Series / Intervalos */}
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '0.65rem 0.8rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Zap size={11} color="var(--strava-orange)" /> {isCoachView ? 'Tolerancia a Series' : '¿Series?'}
                </span>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: dailyPrescription.intervals?.startsWith('Sí') ? '#10b981' : dailyPrescription.intervals === 'Opcional' ? '#3b82f6' : '#f59e0b', marginTop: '3px' }}>
                  {dailyPrescription.intervals}
                </div>
              </div>

              {/* Zonas Objetivo (Spans 2 cols) */}
              <div style={{ gridColumn: 'span 2', background: 'rgba(0,0,0,0.3)', padding: '0.65rem 0.8rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Activity size={11} color={catTheme.color} /> {isCoachView ? 'Techo Fisiológico Sugerido (Umbrales)' : 'Zonas Objetivo (Umbrales)'}
                </span>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'white', marginTop: '3px' }}>
                  {dailyPrescription.targetZones}
                </div>
              </div>

              {/* Qué Evitar Hoy */}
              <div style={{ gridColumn: 'span 2', background: 'rgba(0,0,0,0.3)', padding: '0.65rem 0.8rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Ban size={11} color="#f87171" /> {isCoachView ? 'Restricción Clave para la Prescripción' : 'Qué Evitar Hoy'}
                </span>
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)', marginTop: '2px', lineHeight: 1.3 }}>
                  {dailyPrescription.whatToAvoid}
                </div>
              </div>
            </div>

            {/* Clinical Alerts si existen */}
            {dailyPrescription.alerts?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', position: 'relative', zIndex: 1 }}>
                {dailyPrescription.alerts.map((al, idx) => (
                  <div key={`alert-${idx}`} style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '0.5rem 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#fca5a5',
                    fontSize: '0.74rem',
                    fontWeight: 500
                  }}>
                    <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0 }} />
                    <span>{al}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SÍNTESIS DIARIA DEL COACH IA (ANCHO COMPLETO 24/7) */}
        {!isCoachView && (
          <div style={{ width: '100%', position: 'relative', zIndex: 1 }}>
            <MorningBriefingBanner 
              briefing={effectiveBriefing} 
              loading={briefingLoading && !briefing} 
              isCoachView={isCoachView} 
              userProfile={userProfile} 
              phase={phase}
              hasTrainedToday={hasTrainedToday}
              physiologicalPhase={resolvedPhase}
            />
          </div>
        )}

        {/* FILA INFERIOR: BENTO GRID COMPACTO DE LOS 5 PILARES BIOMÉTRICOS (MODO PRO) */}
        {dashboardMode === 'pro' && (
          <div style={{ marginTop: '0.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Pilares Biométricos de Recuperación
              </span>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                Biometría matinal + Carga acumulada
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '0.65rem'
            }}>
            {/* 1. HRV FACTOR */}
            {(() => {
              const isGood = hrvData.current >= hrvData.average;
              const accent = '#00e5ff';
              return (
                <div style={{
                  background: `radial-gradient(ellipse at top left, ${accent}12 0%, rgba(15,23,42,0.85) 60%, rgba(10,15,25,0.9) 100%)`,
                  border: `1px solid ${accent}25`,
                  borderRadius: '12px', padding: '0.75rem 0.85rem',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.35rem',
                  boxShadow: `0 4px 15px ${accent}05`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Heart size={12} style={{ color: accent }} /> HRV
                    </span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: isGood ? '#10b981' : '#f59e0b' }}>
                      {isGood ? 'Óptimo' : 'Bajo'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif' }}>
                      {Math.round(hrvData.current)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: accent, fontWeight: 600, marginLeft: '3px' }}>ms</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                    Basal: {Math.round(hrvData.average)} ms
                  </div>
                </div>
              );
            })()}

            {/* 2. SUEÑO FACTOR */}
            {(() => {
              const score = sleepData.currentScore;
              const accent = '#8b5cf6';
              const isGood = score >= 70;
              const statusColor = score ? (isGood ? '#10b981' : '#ef4444') : 'var(--text-secondary)';
              const statusLabel = score ? (score >= 80 ? 'Profundo' : score >= 65 ? 'Suficiente' : 'Bajo') : '--';
              return (
                <div style={{
                  background: `radial-gradient(ellipse at top left, ${accent}12 0%, rgba(15,23,42,0.85) 60%, rgba(10,15,25,0.9) 100%)`,
                  border: `1px solid ${accent}25`,
                  borderRadius: '12px', padding: '0.75rem 0.85rem',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.35rem',
                  boxShadow: `0 4px 15px ${accent}05`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Clock size={12} style={{ color: accent }} /> Sueño
                    </span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: statusColor }}>
                      {statusLabel}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif' }}>
                      {score !== null && score !== undefined ? score : '--'}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: accent, fontWeight: 600, marginLeft: '3px' }}>/100</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                    Ponderación: 35%
                  </div>
                </div>
              );
            })()}

            {/* 3. FCR FACTOR */}
            {(() => {
              const isGood = rhrData.current <= rhrData.average;
              const accent = isGood ? '#10b981' : '#ef4444';
              return (
                <div style={{
                  background: `radial-gradient(ellipse at top left, ${accent}12 0%, rgba(15,23,42,0.85) 60%, rgba(10,15,25,0.9) 100%)`,
                  border: `1px solid ${accent}25`,
                  borderRadius: '12px', padding: '0.75rem 0.85rem',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.35rem',
                  boxShadow: `0 4px 15px ${accent}05`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Activity size={12} style={{ color: accent }} /> FC Reposo
                    </span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: accent }}>
                      {isGood ? 'Estable' : `+${rhrData.current - rhrData.average} ppm`}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif' }}>
                      {rhrData.current}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: accent, fontWeight: 600, marginLeft: '3px' }}>ppm</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                    Basal: {rhrData.average} ppm
                  </div>
                </div>
              );
            })()}

            {/* 4. SPO2 FACTOR */}
            {(() => {
              const val = spo2Data?.current != null ? Math.round(spo2Data.current) : null;
              const isGood = val == null || val >= 94;
              const isModerate = val != null && val >= 91 && val < 94;
              const accent = isGood ? '#10b981' : isModerate ? '#f59e0b' : '#ef4444';
              const label = val == null ? '--' : isGood ? 'Normal' : isModerate ? 'Leve var.' : 'Hipoxia';
              return (
                <div style={{
                  background: `radial-gradient(ellipse at top left, ${accent}12 0%, rgba(15,23,42,0.85) 60%, rgba(10,15,25,0.9) 100%)`,
                  border: `1px solid ${accent}25`,
                  borderRadius: '12px', padding: '0.75rem 0.85rem',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.35rem',
                  boxShadow: `0 4px 15px ${accent}05`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Activity size={12} style={{ color: accent }} /> SpO2
                    </span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: accent }}>
                      {label}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif' }}>
                      {val != null ? val : '--'}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: accent, fontWeight: 600, marginLeft: '3px' }}>%</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                    Oxigenación SNC
                  </div>
                </div>
              );
            })()}

            {/* 5. CARGA / STRAIN FACTOR */}
            {(() => {
              const isOverreaching = strainScore >= 14;
              const isProductive = strainScore >= 8;
              const accent = isOverreaching ? '#ef4444' : isProductive ? '#FC4C02' : '#10b981';
              const label = isOverreaching ? 'Alto' : isProductive ? 'Productivo' : 'Bajo';
              return (
                <div style={{
                  background: `radial-gradient(ellipse at top left, ${accent}12 0%, rgba(15,23,42,0.85) 60%, rgba(10,15,25,0.9) 100%)`,
                  border: `1px solid ${accent}25`,
                  borderRadius: '12px', padding: '0.75rem 0.85rem',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.35rem',
                  boxShadow: `0 4px 15px ${accent}05`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Zap size={12} style={{ color: accent }} /> Carga TSB
                    </span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: accent }}>
                      {label}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif' }}>
                      {strainScore}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: accent, fontWeight: 600, marginLeft: '3px' }}>/21</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                    Estrés agudo (ATL)
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        )}
        </>
      )}

      {/* RENDER TAB: HRV PASILLO BIOMÉTRICO (HRV4Training Style) */}
      {activeTab === 'hrv_corridor' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0 }}>Pasillo Biométrico de Variabilidad Cardíaca</h3>
            <p className="text-muted text-xs" style={{ marginTop: '2px' }}>
              Tu HRV diario (fino/cian) y su tendencia de 7 días (verde) cruzando el pasillo de 21 días (sombra).
            </p>
          </div>

          <div style={{ width: '100%', height: '170px', minWidth: 0, minHeight: '170px', marginTop: '0.5rem' }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={170}>
              <ComposedChart data={hrvChartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <defs>
                  <linearGradient id="corridorColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00e5ff" stopOpacity={0.16}/>
                    <stop offset="95%" stopColor="#00e5ff" stopOpacity={0.03}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="day" stroke="var(--text-secondary)" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis domain={['dataMin - 5', 'dataMax + 5']} stroke="var(--text-secondary)" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: '#111827', border: '1px solid var(--glass-border)', borderRadius: '10px' }}
                  labelFormatter={(lbl) => `Día histórico: ${lbl}`}
                />
                
                {/* Biometric corridor limits */}
                <Area type="monotone" dataKey="max" baseValue="min" stroke="none" fill="url(#corridorColor)" name="Pasillo Biométrico" />
                <Line dataKey="max" stroke="rgba(255,255,255,0.12)" strokeWidth={1} strokeDasharray="3 3" dot={false} activeDot={false} name="Límite Superior" />
                <Line dataKey="min" stroke="rgba(255,255,255,0.12)" strokeWidth={1} strokeDasharray="3 3" dot={false} activeDot={false} name="Límite Inferior" />
                
                {/* Active HRV path */}
                <Line type="monotone" dataKey="hrv" stroke="#00e5ff" strokeWidth={1.5} strokeOpacity={0.6} dot={false} activeDot={{ r: 3 }} name="HRV Diario" />
                <Line type="monotone" dataKey="rolling7" stroke="#10b981" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} name="Tendencia 7d" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          
          <div style={{
            background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: '8px',
            padding: '0.5rem 0.8rem', fontSize: '0.72rem', color: '#e0f7fa', lineHeight: 1.35
          }}>
            ℹ️ <strong>Interpretación:</strong> La línea verde (tendencia de 7 días) filtra el ruido diario. Si se mantiene estable **dentro** del pasillo ensombrecido, estás absorbiendo la carga correctamente. Caídas por debajo del pasillo indican estrés acumulado y necesidad de descanso.
          </div>
        </div>
      )}

      {/* RENDER TAB: WHOOP-STYLE STRAIN VS RECOVERY BALANCE */}
      {activeTab === 'strain_balance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0 }}>Carga Diaria (Strain) vs. Recuperación</h3>
            <p className="text-muted text-xs" style={{ marginTop: '2px' }}>
              Contraste del esfuerzo relativo (barras naranjas) frente a la asimilación matinal (línea cian neón).
            </p>
          </div>

          <div style={{ width: '100%', height: '170px', minWidth: 0, minHeight: '170px', marginTop: '0.5rem' }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={170}>
              <ComposedChart data={strainHistory} margin={{ top: 10, right: -15, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="day" stroke="var(--text-secondary)" fontSize={10} tickLine={false} axisLine={false} />
                {/* Left axis for Strain (0 to 21) */}
                <YAxis yAxisId="left" domain={[0, 21]} stroke="var(--strava-orange)" fontSize={10} tickLine={false} axisLine={false} />
                {/* Right axis for Recovery (0 to 100) */}
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} stroke="#00e5ff" fontSize={10} tickLine={false} axisLine={false} />
                
                <Tooltip
                  contentStyle={{ background: '#111827', border: '1px solid var(--glass-border)', borderRadius: '10px' }}
                />
                
                {/* Strain bars */}
                <Bar yAxisId="left" dataKey="strain" fill="var(--strava-orange)" radius={[4, 4, 0, 0]} barSize={16} name="Carga (Strain)" />
                
                {/* Recovery line */}
                <Line yAxisId="right" type="monotone" dataKey="recovery" stroke="#00e5ff" strokeWidth={2.5} dot={{ r: 3, fill: '#00e5ff' }} name="Recuperación" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          
          <div style={{
            background: 'rgba(252,76,2,0.05)', border: '1px solid rgba(252,76,2,0.15)', borderRadius: '8px',
            padding: '0.5rem 0.8rem', fontSize: '0.72rem', color: '#ffebee', lineHeight: 1.35
          }}>
            🎯 <strong>Equilibrio del Atleta:</strong> El Strain diario combina tus entrenamientos con tu actividad pasiva diaria (pasos y NEAT) de tu fuente de salud principal. Un Strain que supere ligeramente la barra de Recuperación optimiza la supercompensación, pero vigila la fatiga no deportiva.
          </div>
        </div>
      )}

      {/* RENDER TAB: ATHLETE DECISION ENGINE 2.0 (NOUSMETRICS v2.5 / v3.0) */}
      {activeTab === 'decision' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'relative', zIndex: 1 }}>
          <DecisionDashboard
            decisionState={resolvedDecisionState}
            readinessData={data}
            clinicalProfile={userProfile?.physiology}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: '1.25rem' }}>
            <MechanicalExposureCard exposure={resolvedExposure} />
            <DataQualityCard quality={resolvedQuality} />
          </div>
        </div>
      )}

      {/* AVISO MÉDICO Y LEGAL DEPORTIVO (Apple Guideline 1.4.1) */}
      <MedicalDisclaimer compact className="mt-3" />

    </section>
  );
};

export default TrainingReadiness;
